import Flickity from 'flickity';

/**
 * SearchSentence
 */
export default class SearchSentence {
    rootPath = '';
    isLoading = false;
    form =null;
    isTypeModalOpen = false;
    isLocationModalOpen = false;
    isExperienceModalOpen = false;
    isMonthModalOpen = false;
    typeModalOffset = 0;
    locationModalOffset = 0;
    experienceModalOffset = 0;
    monthModalOffset = 0;
    type = null;
    location = '';
    month = '';
    experience = '';
    searchQuery = '';
    searchExperienceQuery ='';
    locations = [];
    filteredLocations = [];
    experiences = [];
    filteredExperiences = [];
    resultsCount = 0;
    resultsCountPanelDocked = false;
    activeTab = 'locations';
    tripLayerInitialized = false;
    changingText = '';

    constructor(root, type, location, experience, month, resultsCount) {
        this.rootPath = root;
        this.type = type;
        this.location = location;
        this.experience = experience;
        this.month = month;
        this.resultsCount = resultsCount;
        this.activeTab = this.experience !== '' ? 'experiences' : 'locations';
    }
    init() {
        this.form = document.getElementById('search-sentence-form');

        this.loadDropdowns();
        this.loadResultsCount();
        this.togglePagination();
        this.setModalPositions();
        this.initCards();
        this.setResultsCountPanelDocked();
        this.loadMapLocations();
        this.observeButtonVisibility();
        if (window.addEventListener) {
            window.addEventListener('popstate', () => {
                if (window.history.state === null) {
                    location.reload();
                } else {
                    window.location = window.history.state.path + '&ajax_reload=true';
                }
            });
            window.addEventListener('pageshow', (event) => {
                if (event.persisted) {
                    if (this.type === 'trip') {
                        this.resetMapLocations();
                        this.updateVisibleMarkers();
                    }
                }
            })
        }

        const updateMapTypeEvent = new CustomEvent('update-map-type', {
            detail: {
                type: this.type,
            },
        });
        window.dispatchEvent(updateMapTypeEvent);
        if (this.type === 'trip') {
            setTimeout(() => {
                this.updateVisibleMarkers();
            }, 1000);
        }
        pegasus.deleteCookie('lastSentencePage');
        pegasus.setCookie('lastSentencePage', window.location.href, 1);
        pegasus.deleteCookie('lastExplorePage');
        pegasus.setCookie('lastExplorePage', window.location.href, 1);
    }

    /**
     * Loads the dropdown data from the form dataset
     */
    loadDropdowns() {
        this.loadLocations();
        this.loadExperiences();
        this.filteredLocations = this.locations;
        this.filteredExperiences = this.experiences;
    }

    loadLocations() {
        try {
            this.locations = JSON.parse(this.form.dataset.locations);
        } catch (error) {
            console.error('Error in SearchSentence init', error);
            this.locations = [];
        }
    }

    loadExperiences() {
        try {
            this.experiences = JSON.parse(this.form.dataset.experiences);
        } catch (error) {
            console.error('Error in SearchSentence init', error);
            this.experiences = [];
        }
    }

    loadResultsCount() {
        try {
            this.resultsCount = JSON.parse(this.form.dataset.resultsCount);
        } catch (error) {
            console.error('Error in SearchSentence init', error);
            this.resultsCount = 0;
        }
    }

    loadMapLocations() {
        const locations = Array.from(document.querySelectorAll('.SearchSentenceCard'))
            .filter(card => card?.dataset?.lng && card?.dataset?.lat)
            .map(card => {
                // Base location from the card's lat/lng attributes
                const baseLocation = {
                    id: card.dataset.id,
                    type: (card.dataset.type) ? card.dataset.type : this.type,
                    coordinates: [
                        card.dataset.lng,
                        card.dataset.lat,
                    ],
                };

                // Parse additional markers if present
                const additionalMarkers = card.dataset.additionalMarkers
                    ? JSON.parse(card.dataset.additionalMarkers)
                    : [];

                // Map additional markers into the same format as base locations
                const additionalLocations = additionalMarkers.map(marker => ({
                    id: marker.id,
                    type: (marker.type) ? marker.type : this.type,
                    coordinates: [
                        marker.lng,
                        marker.lat,
                    ],
                }));

                // Return the base location and merge additional locations
                return [baseLocation, ...additionalLocations];
            })
            .flat(); // Flatten the array since `map` returns nested arrays

        const updateMapLocationsEvent = new CustomEvent('update-map-locations', {
            detail: {
                locations,
                type: this.type,
            },
        });

        window.dispatchEvent(updateMapLocationsEvent);
    }

    resetMapLocations() {
        const resetMapLocationsEvent = new CustomEvent('update-map-locations', {
            detail: {
                locations: [],
                type: this.type,
            },
        });
        window.dispatchEvent(resetMapLocationsEvent);
    }


    /**
     * Model closing / opening methods
     */
    openTypeModal() {
        this.isTypeModalOpen = true;

        this.setTypeModalPosition();
    }
    closeTypeModal() {
        this.isTypeModalOpen = false;
    }
    openLocationModal() {
        this.isLocationModalOpen = true;

        this.setLocationModalPosition();

        setTimeout(() => {
            this.$refs.locationsSearch.focus();
        }, 100);

    }
    closeLocationModal() {
        this.isLocationModalOpen = false;
    }
    openExperienceModal() {
        this.isExperienceModalOpen = true;

        this.setExperienceModalPosition();

        setTimeout(() => {
            this.$refs.experienceSearch.focus();
        }, 100);
    }
    closeExperienceModal() {
        this.isExperienceModalOpen = false;
    }
    openMonthModal() {
        this.isMonthModalOpen = true;

        this.setMonthModalPosition();
    }
    closeMonthModal() {
        this.isMonthModalOpen = false;
    }

    getChangingText() {
        return this.changingText;
    }

    /**
     * change the tab on location/experience combined dropdown
     * @param tab
     */
    setActiveTab(tab) {
        this.activeTab = tab;
        if (tab === 'locations') {
            this.$refs.locationsSearch.focus();
            this.location = '';
            this.month = '';
            this.experience = '';
        } else {
            this.$refs.experienceSearch.focus();
            this.location = '';
            this.month = '';
            this.experience = '';
        }
    }

    /**
     * Selector methods
     */
    selectType(type) {
        if (this.type !== type) {
            // recall the page with the new type
            const oldType = this.type;
            this.type = type;
            const formData = new FormData(this.form);
            if (type === 'trip' || oldType === 'trip') {
                this.experience = '';
                formData.set('filter-experience', '');
                this.location = '';
                formData.set('filter-location', '');
                this.month = '';
                formData.set('filter-month', '');
                this.changingText = this.getChangingTextFor(type);
            } else if (type === 'location') {
                this.location = '';
                formData.set('filter-location', '');
                this.changingText = this.getChangingTextFor('location')
            } else {
                this.changingText = this.getChangingTextFor('location')
            }
            // change "filter-type"
            formData.set('filter-type', type);
            const searchParams = new URLSearchParams(formData).toString();
            const url = new URL(window.location.href);
            // navigate to the new url
            this.isLoading = true;
            this.closeTypeModal();
            window.location.href = url.pathname + '?' + searchParams;
            return;
        }
        this.type = type;
        this.experience = '';
        this.location = '';
        this.month = '';
        this.closeTypeModal();
        setTimeout(() => {
            this.handleSubmit(null);
        }, 100);
    }
    getChangingTextFor(type) {
        if (type === 'trip') {
            return 'including ' + this.getLocationLabel();
        } else {
            return this.getLocationLabel() + ' for ' + this.getExperienceLabel() + ' in ' + this.getMonthLabel();
        }
    }

    selectLocation(location) {
        this.location = location;
        this.closeLocationModal();
        setTimeout(() => {
            this.handleSubmit(null);
        }, 100);
    }
    selectExperience(experience) {
        this.experience = experience;
        this.closeLocationModal(); // due to the way the modals are set up, we close the location modal here
        this.closeExperienceModal();
        setTimeout(() => {
            this.handleSubmit(null);
        }, 100);
    }

    selectMonth(month) {
        this.month = month;
        this.closeMonthModal();
        setTimeout(() => {
            this.handleSubmit(null);
        }, 100);
    }

    /**
     * Filtering in the dropdowns
     */
    filterLocations() {
        const query = this.searchQuery.toLowerCase();
        if (query) {
            const startsWithQuery = this.locations.filter(location =>
                location.toLowerCase().startsWith(query)
            );
            const includesQuery = this.locations.filter(location =>
                !location.toLowerCase().startsWith(query) &&
                location.toLowerCase().includes(query)
            );
            this.filteredLocations = [...startsWithQuery, ...includesQuery];
        } else {
            this.filteredLocations = this.locations;
        }
    }
    filterExperiences() {
        const query = this.searchExperienceQuery.toLowerCase();
        if (query) {
            const startsWithQuery = this.experiences.filter(experience =>
                experience.toLowerCase().startsWith(query)
            );
            const includesQuery = this.experiences.filter(experience =>
                !experience.toLowerCase().startsWith(query) &&
                experience.toLowerCase().includes(query)
            );
            this.filteredExperiences = [...startsWithQuery, ...includesQuery];
        } else {
            this.filteredExperiences = this.experiences;
        }
    }

    /**
     * Labels for the search sentence
     */
    getLocationLabel() {
        return this.location ? this.location : ((this.type === 'trip') ? 'Africa' : 'the World');
    }

    getLocationOrExperienceLabel() {
        return this.location ? this.getLocationLabel() : (this.experience ? this.getExperienceLabel() : ((this.type === 'trip') ? 'any location' : 'the World'));
    }

    getMonthLabel() {
        const months = {
            'jan': 'January',
            'feb': 'February',
            'mar': 'March',
            'apr': 'April',
            'may': 'May',
            'jun': 'June',
            'jul': 'July',
            'aug': 'August',
            'sep': 'September',
            'oct': 'October',
            'nov': 'November',
            'dec': 'December'
        };
        return this.month ? months[this.month] : 'any month';
    }
    getExperienceLabel() {
        return this.experience.toLowerCase() || 'any experience';
    }

    /**
     * Formatting for experience labels
     * @param experience
     * @returns {*}
     */
    displayExperience(experience) {
        return experience
            .replace(/_/g, ' ')                              // Replace underscores with spaces
            .replace(/\b(?!-)\w/g, l => l.toUpperCase())     // Capitalize first letters except after hyphens
            .replace(/-\w/g, match => match.toLowerCase());  // Ensure letters after hyphens are lowercase
    }

    /**
     * Type dropdown labels
     * @returns {string}
     */
    getTypeLabel() {
       if (this.type === 'experience') {
           return 'experiences';
       } else if (this.type === 'location') {
           return 'locations';
       } else if (this.type === 'lodge') {
           return 'lodges';
       } else {
           return 'trips';
       }
    }

    /**
     * Form submission. Currently triggered by selecting a dropdown option
     * @param e
     * @param append
     */
    handleSubmit(e, append = false) {
        if (e) {
            e.preventDefault();
        }
        if (!append) {
            this.form.querySelector('input[name="filter-page"]').value = 1;
        }
        const formData = new FormData(this.form);
        const searchParams = new URLSearchParams(formData).toString();
        const url = new URL(window.location.href);
        if (!append) {
            this.fetchContent(url.pathname + '?' + searchParams);
        } else {
            this.fetchMoreContent(url.pathname + '?' + searchParams);
        }
    };

    /**
     * Observe the visibility of the pagination button
     */
    observeButtonVisibility() {
        const paginationContainer = document.querySelector('.ElasticFilter__Pagination');
        if (!paginationContainer) {
            return;
        }
        const interval = 200; // Interval in milliseconds for periodic checks
        const checkVisibility = () => {
            if (!this.isContainerVisible(paginationContainer)) {
                return; // Do nothing if the container is not visible
            }
            const button = paginationContainer.querySelector('button');
            if (button && this.isButtonVisible(button, 500) && !this.isLoading) {
                this.loadMore();
            }
        };

        this.visibilityCheckInterval = setInterval(checkVisibility, interval);
    }

    /**
     * Check if the container is visible
     * @param container
     * @returns {boolean}
     */
    isContainerVisible(container) {
        const style = getComputedStyle(container);
        return style.display !== 'none'; // Check if the container is visible
    }

    /**
     * Check if the button is visible
     * @param button
     * @returns {boolean}
     */
    isButtonVisible(button, offset = 0) {
        const rect = button.getBoundingClientRect();
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;

        // Check if the button is in the viewport
        return (rect.top - offset) <= windowHeight && rect.bottom >= 0;
    }

    /**
     * Cleanup method if the component is destroyed. As we normally recall the page, it's not needed.
     * Call this method if no more page reloads are expected.
     */
    cleanup() {
        // Clear the interval when the component is destroyed
        if (this.visibilityCheckInterval) {
            clearInterval(this.visibilityCheckInterval);
        }
    }

    /**
     * Load more results
     */
    loadMore(e) {
        if (e) {
            e.preventDefault();
        }
        if ((parseInt(this.form.querySelector('input[name="filter-page"]').value) * parseInt(this.form.querySelector('input[name="filter-page_size"]').value)) > parseInt(this.form.querySelector('input[name="total"]').value)) {
            return;
        }
        this.form.querySelector('input[name="filter-page"]').value = parseInt(this.form.querySelector('input[name="filter-page"]').value) + 1;
        this.handleSubmit(e, true)
    }

    /**
     * Ajax fetch content
     * @param url
     */
    fetchContent(url) {
        const elements= ['data-pegasus-filterable="filters"', 'data-pegasus-filterable="results"'];
        //show LoadingScreen
        this.isLoading = true;
        const historyUrl = url;
        pegasus.ajaxContent(url, elements, {}, () => {
            this.form = document.getElementById('search-sentence-form');
            if (this.type === 'trip'){
                this.resetMapLocations();
                this.updateVisibleMarkers();
            } else {
                this.loadMapLocations();
            }
            this.loadDropdowns();
            this.loadResultsCount();
            this.togglePagination();
            this.pushToHistory(document.title, historyUrl);
            this.initCards();
            this.observeButtonVisibility();
            this.searchQuery = '';
            this.searchExperienceQuery = '';
            this.isLoading = false;

            pegasus.deleteCookie('lastSentencePage');
            pegasus.setCookie('lastSentencePage', window.location.href, 1);
            pegasus.deleteCookie('lastExplorePage');
            pegasus.setCookie('lastExplorePage', window.location.href, 1);
        });
    }

    initCards() {
        const cardCarousels = document.querySelectorAll('.SearchSentenceCard__Carousel');

        if (!cardCarousels) return;

        cardCarousels.forEach(cardCarousel => {
            this.carousel = new Flickity(cardCarousel, {
                prevNextButtons: true,
            });
        });
    }

    /**
     * Fetch the content and append it
     * @param url
     */
    fetchMoreContent(url) {
        const elements = ['data-pegasus-filterable="results"'];

        this.isLoading = true;

        const historyUrl = url;
        pegasus.ajaxFetchAndAppendContent(url, elements, {}, () => {
            this.loadMapLocations();
            this.togglePagination();
            this.observeButtonVisibility();
            this.pushToHistory(document.title, historyUrl);
            this.isLoading = false;
        });
    }

    /**
     * Toggle the visibility of the pagination button
     */
    togglePagination() {
        const p = document.querySelector('.ElasticFilter__Pagination');
        if (!p) return;
        if ((parseInt(this.form.querySelector('input[name="filter-page"]').value) * parseInt(this.form.querySelector('input[name="filter-page_size"]').value)) < parseInt(this.form.querySelector('input[name="total"]').value)) {
            p.style.display = 'block';
        } else {
            p.style.display = 'none';
        }
    }

    /**
     * Check if the browser supports history
     * @returns {boolean}
     */
    historySupported() {
        return !!(window.history && history.pushState);
    }

    /**
     * pushing a title and an url into the history
     * @param title
     * @param url
     */
    pushToHistory(title, url) {
        if (this.historySupported()) {
            history.pushState({ path: url }, title, url);
            pegasus.setCookie('lastExplorePage', window.location.href, 1);
        }
    }

    /**
     * Returns element right overflow relative to body
     * @param el
     * @returns {number}
     */
    getElementOverflow(el) {
        return el.getBoundingClientRect().left + el.offsetWidth - document.body.clientWidth;
    }

    /**
     * Adjusts all modal positions if off screen 
     */
    setModalPositions() {
        this.setTypeModalPosition();
        this.setLocationModalPosition();
        this.setExperienceModalPosition();
        this.setMonthModalPosition();
    }

    /**
     * Adjusts type modal position if off screen 
     */
    setTypeModalPosition() {
        if (!this.$refs?.typeModal) return;

        const rightOverflow = this.getElementOverflow(this.$refs.typeModal);

        this.typeModalOffset = Math.ceil(rightOverflow > 0 ? -rightOverflow : 0);
    }

    /**
     * Adjusts location modal position if off screen 
     */
    setLocationModalPosition() {
        if (!this.$refs?.locationModal) return;

        const rightOverflow = this.getElementOverflow(this.$refs.locationModal);

        this.locationModalOffset = Math.ceil(rightOverflow > 0 ? -rightOverflow : 0);
    }

    /**
     * Adjusts experience modal position if off screen 
     */
    setExperienceModalPosition() {
        if (!this.$refs?.experienceModal) return;

        const rightOverflow = this.getElementOverflow(this.$refs.experienceModal);

        this.experienceModalOffset = Math.ceil(rightOverflow > 0 ? -rightOverflow : 0);
    }

    /**
     * Adjusts month modal position if off screen 
     */
    setMonthModalPosition() {
        if (!this.$refs?.monthModal) return;

        const rightOverflow = this.getElementOverflow(this.$refs.monthModal);

        this.monthModalOffset = Math.ceil(rightOverflow > 0 ? -rightOverflow : 0);
    }

    /**
     * Scroll to top of screen 
     */
    goToTop(e) {
        e.preventDefault();

        window.scrollTo({
            top: 0, 
            left: 0,
            behavior: 'smooth',
        });
    }

    /**
     * Checks if the results count panel is docked 
     */
    setResultsCountPanelDocked() {
        this.resultsCountPanelDocked = this.$refs.resultsCountPanel.getBoundingClientRect().top <= (this.$refs.navPanel.clientHeight + 3);
    }

    goToLastPage() {
        if (pegasus.getCookie('lastPage')) {
            window.location.href = pegasus.getCookie('lastPage');
        } else {
            window.location.href = '/';
        }

    }
    updateVisibleMarkers() {
        // Get all result elements
        if (this.type !== 'trip') {
            return;
        }
        let visibleCoordinates = [];
        // Check each result to see if it's in the viewport
        const result = this.getElementWithLargestVisibleArea(document.querySelectorAll('.Trip_Result'));
        if (result) {
            const coordinates = JSON.parse(result.getAttribute('data-coordinates'));
            visibleCoordinates = visibleCoordinates.concat(coordinates);
        }
        if (visibleCoordinates.length === 0 && document.querySelectorAll('.Trip_Result').length > 0 && !this.tripLayerInitialized) {
            // If no results are in the viewport, use the first result
            const coordinates = JSON.parse(document.querySelectorAll('.Trip_Result')[0].getAttribute('data-coordinates'));
            visibleCoordinates = visibleCoordinates.concat(coordinates);
            this.tripLayerInitialized = true
        }

        // Update map with visible coordinates
        if (visibleCoordinates.length > 0) {
            this.resetMapLocations();
            console.log('Updating map with visible coordinates', visibleCoordinates);
            const updateMapLocationsEvent = new CustomEvent('update-map-locations', {
                detail: {
                    locations: visibleCoordinates,
                    type: this.type,
                },
            });

            window.dispatchEvent(updateMapLocationsEvent);
        }
    }

    getElementWithLargestVisibleArea(els) {
        let maxVisibleArea = 0;
        let elementWithMaxArea = null;

        els.forEach(el => {
            const rect = el.getBoundingClientRect();
            const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

            const visibleWidth = Math.max(0, Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0));
            const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
            const visibleArea = visibleWidth * visibleHeight;

            if (visibleArea > maxVisibleArea) {
                maxVisibleArea = visibleArea;
                elementWithMaxArea = el;
            }
        });

        return elementWithMaxArea;
    }
}

document.addEventListener('alpine:init', () => {
    Alpine.data('searchSentence', (root, type, location, experience, month, resultsCount) => new SearchSentence(root, type, location, experience, month, resultsCount));
});
